import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Trees,
  Camera,
  Wallet,
  ClipboardCheck,
  Layers,
  Map as MapIcon,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Download,
  FolderDown,
} from 'lucide-react';
import type { ImportCommitResponse, ImportParseResponse, ImportMode } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { useImportCommit, useImportParse, type ImportTarget } from './useImport';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/sessionStore';
import { apiError } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { DriveImportDialog } from '@/features/photos/DriveImportDialog';
import { ShapefileImportPanel } from './ShapefileImportPanel';
import { SubParcelImportPanel } from './SubParcelImportPanel';
import { ReceptiiImportPanel } from './ReceptiiImportPanel';
import { WorkImportPanel } from './WorkImportPanel';

type Step = 'upload' | 'preview' | 'mapping' | 'commit' | 'done';

const STEPS: Step[] = ['upload', 'preview', 'mapping', 'commit'];

export function ImportWizard() {
  const { t } = useTranslation(['import', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qsTarget = searchParams.get('target');
  const initialTarget: ImportTarget =
    qsTarget === 'photo'
      ? 'photo'
      : qsTarget === 'parcel-shp'
        ? 'parcel-shp'
        : qsTarget === 'receptie'
          ? 'receptie'
          : qsTarget === 'subparcel'
            ? 'subparcel'
            : 'parcel';

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [target, setTarget] = useState<ImportTarget>(initialTarget);
  // Divergent targets swap the whole body for a dedicated panel.
  const custom =
    target === 'parcel-shp' ||
    target === 'work' ||
    target === 'receptie' ||
    target === 'subparcel';
  const [parsed, setParsed] = useState<ImportParseResponse | null>(null);
  const [committed, setCommitted] = useState<ImportCommitResponse | null>(null);
  const [mode, setMode] = useState<ImportMode>('skip_invalid');
  const [dragOver, setDragOver] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'SUPER_ADMIN');
  const parse = useImportParse();
  const commit = useImportCommit();
  const pushToast = useUiStore((s) => s.pushToast);

  const onParse = async () => {
    if (!file) { pushToast({ variant: 'error', message: t('error.noFile') }); return; }
    try {
      const result = await parse.mutateAsync({ file, target });
      setParsed(result);
      setStep('preview');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const onCommit = async () => {
    if (!parsed) return;
    try {
      const result = await commit.mutateAsync({ batchId: parsed.batchId, mode });
      setCommitted(result);
      setStep('done');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const templateUrl = target === 'photo'
    ? '/api/parcels/import/template?format=xlsx&target=photo'
    : '/api/parcels/import/template?format=xlsx';

  const doneRoute = target === 'photo' ? '/app/database' : '/app/parcels';
  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="space-y-6">
      {/* Target selector — always visible. Switching to "Shapefile (PNERP)"
          swaps the entire body out to the dedicated <ShapefileImportPanel>
          because the upload + preview shape differs from xlsx/csv. */}
      <div>
        <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
          {t('target.label')}
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <button
            type="button"
            onClick={() => setTarget('parcel')}
            className={cn(
              'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
              target === 'parcel'
                ? 'border-forest-500 bg-forest-50 dark:border-forest-500 dark:bg-forest-900/20'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                target === 'parcel'
                  ? 'bg-forest-500 text-white'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
              )}
            >
              <Trees className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">{t('target.parcel')}</div>
              <div className="text-xs text-slate-500">meta_3.xlsx</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTarget('photo')}
            className={cn(
              'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
              target === 'photo'
                ? 'border-sky-500 bg-sky-50 dark:border-sky-500 dark:bg-sky-900/20'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                target === 'photo'
                  ? 'bg-sky-500 text-white'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
              )}
            >
              <Camera className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">{t('target.photo')}</div>
              <div className="text-xs text-slate-500">meta_3_photo.xlsx</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTarget('parcel-shp')}
            className={cn(
              'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
              target === 'parcel-shp'
                ? 'border-violet-500 bg-violet-50 dark:border-violet-500 dark:bg-violet-900/20'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                target === 'parcel-shp'
                  ? 'bg-violet-500 text-white'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
              )}
            >
              <MapIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">
                {t('target.shapefile', { defaultValue: 'Shapefile (PNERP)' })}
              </div>
              <div className="text-xs text-slate-500">UCF_PNERP_2022_2025.shp</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTarget('receptie')}
            className={cn(
              'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
              target === 'receptie'
                ? 'border-sky-500 bg-sky-50 dark:border-sky-500 dark:bg-sky-900/20'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                target === 'receptie'
                  ? 'bg-sky-500 text-white'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
              )}
            >
              <ClipboardCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">
                {t('target.receptie', { defaultValue: 'Subpoligoane din recepții' })}
              </div>
              <div className="text-xs text-slate-500">Primarie_UCF_parcele_ha.zip</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTarget('subparcel')}
            className={cn(
              'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
              target === 'subparcel'
                ? 'border-orange-500 bg-orange-50 dark:border-orange-500 dark:bg-orange-900/20'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg',
                target === 'subparcel'
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
              )}
            >
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold">
                {t('target.subparcel', { defaultValue: 'Subpoligoane' })}
              </div>
              <div className="text-xs text-slate-500">UCF_sub = 1A1 (.shp)</div>
            </div>
          </button>
          {isSuperAdmin ? (
            <button
              type="button"
              onClick={() => setTarget('work')}
              className={cn(
                'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
                target === 'work'
                  ? 'border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-900/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  target === 'work'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
                )}
              >
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold">
                  {t('target.work', { defaultValue: 'Lucrări & cheltuieli' })}
                </div>
                <div className="text-xs text-slate-500">Pnerp_*_3.xlsx</div>
              </div>
            </button>
          ) : null}
        </div>
      </div>

      {target === 'parcel-shp' ? <ShapefileImportPanel /> : null}
      {target === 'work' ? <WorkImportPanel /> : null}
      {target === 'receptie' ? <ReceptiiImportPanel /> : null}
      {target === 'subparcel' ? <SubParcelImportPanel /> : null}

      {/* Step indicator */}
      {!custom && step !== 'done' && (
        <div className="flex items-center justify-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all',
                  i < stepIndex
                    ? 'bg-forest-500 text-white'
                    : i === stepIndex
                      ? 'bg-forest-600 text-white ring-4 ring-forest-100 dark:ring-forest-900/40'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
                )}
              >
                {i < stepIndex ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  'hidden text-xs font-medium sm:inline',
                  i === stepIndex
                    ? 'text-forest-700 dark:text-forest-300'
                    : 'text-slate-400 dark:text-slate-500',
                )}
              >
                {t(`step.${s}` as const)}
              </span>
              {i < STEPS.length - 1 && (
                <div className={cn('mx-2 h-px w-8', i < stepIndex ? 'bg-forest-400' : 'bg-slate-200 dark:bg-slate-700')} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Upload (xlsx/csv only) */}
      {!custom && step === 'upload' && (
        <div className="space-y-5">
          {/* The original top-of-page target selector (kept as fallback for
              clarity — the new 3-card row above takes the user's eye but
              this preserves the existing visual rhythm of the xlsx flow). */}
          <div className="hidden">
            <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">{t('target.label')}</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTarget('parcel')}
                className={cn(
                  'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
                  target === 'parcel'
                    ? 'border-forest-500 bg-forest-50 shadow-sm shadow-forest-100 dark:border-forest-500 dark:bg-forest-900/20 dark:shadow-none'
                    : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600',
                )}
              >
                <div className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  target === 'parcel'
                    ? 'bg-forest-500 text-white'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
                )}>
                  <Trees className="h-5 w-5" />
                </div>
                <div>
                  <div className={cn(
                    'text-sm font-semibold',
                    target === 'parcel'
                      ? 'text-forest-800 dark:text-forest-200'
                      : 'text-slate-700 dark:text-slate-200',
                  )}>
                    {t('target.parcel')}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">meta_3.xlsx</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTarget('photo')}
                className={cn(
                  'flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all',
                  target === 'photo'
                    ? 'border-sky-500 bg-sky-50 shadow-sm shadow-sky-100 dark:border-sky-500 dark:bg-sky-900/20 dark:shadow-none'
                    : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600',
                )}
              >
                <div className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  target === 'photo'
                    ? 'bg-sky-500 text-white'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
                )}>
                  <Camera className="h-5 w-5" />
                </div>
                <div>
                  <div className={cn(
                    'text-sm font-semibold',
                    target === 'photo'
                      ? 'text-sky-800 dark:text-sky-200'
                      : 'text-slate-700 dark:text-slate-200',
                  )}>
                    {t('target.photo')}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">meta_3_photo.xlsx</div>
                </div>
              </button>
            </div>
          </div>

          {/* Drag and drop area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={cn(
              'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 transition-all',
              dragOver
                ? 'border-forest-500 bg-forest-50 dark:bg-forest-900/20'
                : file
                  ? 'border-forest-300 bg-forest-50/50 dark:border-forest-700 dark:bg-forest-900/15'
                  : 'border-slate-300 bg-slate-50 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-slate-500',
            )}
          >
            {file ? (
              <>
                <FileSpreadsheet className="mb-3 h-10 w-10 text-forest-500" />
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{file.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="mt-2 text-xs text-red-500 hover:underline dark:text-red-400"
                >
                  {t('common:action.delete')}
                </button>
              </>
            ) : (
              <>
                <Upload className="mb-3 h-10 w-10 text-slate-400 dark:text-slate-500" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('upload.drop')}</p>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{t('upload.hint')}</p>
              </>
            )}
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <a
              className="inline-flex items-center gap-1.5 text-sm text-forest-700 hover:underline"
              href={templateUrl}
            >
              <Download className="h-4 w-4" />
              {t('upload.template')}
            </a>
            <Button onClick={onParse} loading={parse.isPending} disabled={!file}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          {/* Alternative: Drive folder import (SUPER_ADMIN + photo target only) */}
          {target === 'photo' && isSuperAdmin && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500 text-white">
                    <FolderDown className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-sky-900">
                      {t('drive.title', { defaultValue: 'Import din Google Drive' })}
                    </p>
                    <p className="text-xs text-sky-700/80">
                      {t('drive.subtitle', {
                        defaultValue:
                          'Import în masă dintr-un folder Drive public (doar SUPER_ADMIN)',
                      })}
                    </p>
                  </div>
                </div>
                <Button variant="secondary" onClick={() => setDriveOpen(true)}>
                  {t('drive.openButton', { defaultValue: 'Deschide Drive' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isSuperAdmin && (
        <DriveImportDialog open={driveOpen} onClose={() => setDriveOpen(false)} />
      )}

      {/* Step 2: Preview (xlsx/csv only) */}
      {!custom && step === 'preview' && parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label={t('preview.rows')} value={parsed.preview.length} color="slate" />
            <StatCard label={t('preview.valid')} value={parsed.validCount} color="green" />
            <StatCard label={t('preview.invalid')} value={parsed.invalidCount} color={parsed.invalidCount > 0 ? 'red' : 'slate'} />
            <StatCard label={t('preview.encoding')} value={parsed.detectedEncoding} color="slate" />
          </div>
          <Card>
            <CardBody className="max-h-72 overflow-auto p-0">
              {target === 'photo' ? (
                <PhotoPreviewTable preview={parsed.preview} />
              ) : (
                <ParcelPreviewTable preview={parsed.preview} />
              )}
            </CardBody>
          </Card>
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep('upload')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <Button onClick={() => setStep('mapping')}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Mapping (xlsx/csv only) */}
      {!custom && step === 'mapping' && parsed && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('mapping.title')}</p>
          <Card>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">{t('mapping.header')}</th>
                    <th className="px-4 py-2.5 text-left font-semibold">{t('mapping.target')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(parsed.headerMapping).map(([from, to]) => (
                    <tr key={from} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-2 text-slate-700 dark:text-slate-200">{from}</td>
                      <td className="px-4 py-2 font-mono text-xs text-forest-700 dark:text-forest-300">{to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep('preview')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <Button onClick={() => setStep('commit')}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Commit (xlsx/csv only) */}
      {!custom && step === 'commit' && parsed && (
        <Card>
          <CardBody className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('commit.mode')}</label>
              <Select value={mode} onChange={(e) => setMode(e.target.value as ImportMode)} className="mt-1">
                <option value="skip_invalid">{t('commit.modeSkip')}</option>
                <option value="all_or_nothing">{t('commit.modeAll')}</option>
              </Select>
            </div>
            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep('mapping')}>
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                {t('common:action.back')}
              </Button>
              <Button onClick={onCommit} loading={commit.isPending}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {t('commit.confirm')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Done */}
      {!custom && step === 'done' && committed && (
        <Card>
          <CardBody className="space-y-5 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
                <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-300" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">{t('result.done')}</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatCard label={t('result.imported')} value={committed.imported} color="green" />
              <StatCard label={t('result.skipped')} value={committed.skipped} color={committed.skipped > 0 ? 'yellow' : 'slate'} />
              <StatCard label={t('result.errors')} value={committed.errors.length} color={committed.errors.length > 0 ? 'red' : 'slate'} />
            </div>
            {committed.errors.length > 0 && (
              <div className="max-h-48 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-left text-xs dark:border-red-500/30 dark:bg-red-500/10">
                <div className="mb-1.5 flex items-center gap-1.5 font-semibold text-red-700 dark:text-red-300">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Erori
                </div>
                <ul className="space-y-0.5 text-red-600 dark:text-red-300">
                  {committed.errors.slice(0, 100).map((er, i) => (
                    <li key={i}>Row {er.row}: {er.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <Button onClick={() => navigate(doneRoute)} className="mx-auto">
              {t('common:action.close')}
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Loading overlay */}
      {(parse.isPending || commit.isPending) && (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-slate-500 dark:text-slate-400">
          <Spinner /> {t('common:state.loading')}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function StatCard({
  label,
  value,
  color = 'slate',
}: {
  label: string;
  value: number | string;
  color?: 'green' | 'red' | 'yellow' | 'slate';
}) {
  const borderColor = {
    green: 'border-l-emerald-500',
    red: 'border-l-red-500',
    yellow: 'border-l-yellow-500',
    slate: 'border-l-slate-300 dark:border-l-slate-600',
  }[color];

  return (
    <div className={cn('rounded-lg border border-slate-200 border-l-4 bg-white p-3 dark:border-slate-700 dark:bg-slate-800', borderColor)}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-0.5 text-lg font-bold text-slate-800 dark:text-slate-100">{value}</p>
    </div>
  );
}

function ParcelPreviewTable({ preview }: { preview: ImportParseResponse['preview'] }) {
  return (
    <table className="table-striped w-full text-xs">
      <thead className="sticky top-0 bg-slate-50/95 text-[10px] uppercase text-slate-500 backdrop-blur-sm dark:bg-slate-800/95 dark:text-slate-400">
        <tr>
          <th className="px-3 py-2 text-left font-semibold">#</th>
          <th className="px-3 py-2 text-left font-semibold">Raion</th>
          <th className="px-3 py-2 text-left font-semibold">Primărie</th>
          <th className="px-3 py-2 text-left font-semibold">UCF</th>
          <th className="px-3 py-2 text-right font-semibold">Suprafață</th>
          <th className="px-3 py-2 text-right font-semibold">An</th>
          <th className="w-8 px-2 py-2 text-center font-semibold">✓</th>
        </tr>
      </thead>
      <tbody>
        {preview.slice(0, 30).map((r) => (
          <tr key={r.row} className={r.valid ? 'border-t border-slate-50 dark:border-slate-800' : 'border-t border-red-100 bg-red-50/60 dark:border-red-900/30 dark:bg-red-500/10'}>
            <td className="px-3 py-1.5 font-mono text-slate-400 dark:text-slate-500">{r.row}</td>
            <td className="px-3 py-1.5">{r.raion ?? ''}</td>
            <td className="px-3 py-1.5">{r.primarie ?? ''}</td>
            <td className="px-3 py-1.5 font-mono">{r.ucf ?? ''}</td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">{r.suprafata ?? ''}</td>
            <td className="px-3 py-1.5 text-right font-mono tabular-nums">{r.anul ?? ''}</td>
            <td className="px-2 py-1.5 text-center">
              {r.valid
                ? <CheckCircle2 className="mx-auto h-3.5 w-3.5 text-emerald-500" />
                : <AlertCircle className="mx-auto h-3.5 w-3.5 text-red-500" />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PhotoPreviewTable({ preview }: { preview: ImportParseResponse['preview'] }) {
  return (
    <table className="table-striped w-full text-xs">
      <thead className="sticky top-0 bg-slate-50/95 text-[10px] uppercase text-slate-500 backdrop-blur-sm">
        <tr>
          <th className="px-3 py-2 text-left font-semibold">#</th>
          <th className="px-3 py-2 text-left font-semibold">File Name</th>
          <th className="px-3 py-2 text-right font-semibold">Lat</th>
          <th className="px-3 py-2 text-right font-semibold">Lon</th>
          <th className="px-3 py-2 text-left font-semibold">Raion</th>
          <th className="px-3 py-2 text-right font-semibold">An</th>
          <th className="w-8 px-2 py-2 text-center font-semibold">✓</th>
        </tr>
      </thead>
      <tbody>
        {preview.slice(0, 30).map((r) => {
          const row = r as Record<string, unknown>;
          return (
            <tr key={r.row} className={r.valid ? 'border-t border-slate-50' : 'border-t border-red-100 bg-red-50/60'}>
              <td className="px-3 py-1.5 font-mono text-slate-400">{r.row}</td>
              <td className="max-w-[160px] truncate px-3 py-1.5">{String(row.fileName ?? '')}</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums">{String(row.latitude ?? '')}</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums">{String(row.longitude ?? '')}</td>
              <td className="px-3 py-1.5">{r.raion ?? ''}</td>
              <td className="px-3 py-1.5 text-right font-mono tabular-nums">{r.anul ?? ''}</td>
              <td className="px-2 py-1.5 text-center">
                {r.valid
                  ? <CheckCircle2 className="mx-auto h-3.5 w-3.5 text-emerald-500" />
                  : <AlertCircle className="mx-auto h-3.5 w-3.5 text-red-500" />}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
